import type { HermesAPI } from "./api";
import { HermesError } from "./errors";
import { arr, bool, int, obj, s, strings, type JsonObject } from "./json";

// 模型服务的进阶配置（对应 iOS 的 ModelServicesAPI.swift），全部是 Hermes 网页后台 / 桌面端设置页用的官方 REST 接口：
// - 自定义接口（OpenAI 兼容地址）：/api/providers/custom-endpoints
// - 辅助模型（看图、压缩、起标题…）：/api/model/auxiliary + /api/model/set scope=auxiliary
// - 生图 / 生视频后端：/api/tools/toolsets/{image_gen|video_gen}/…

// MARK: 自定义接口

export interface CustomEndpoint {
  id: string;
  name: string;
  base_url: string;
  model: string;
  models?: string[];
  /** "" = 自动识别；chat_completions / codex_responses / anthropic_messages */
  api_mode?: string;
  context_length?: number;
  has_api_key?: boolean;
  api_key_preview?: string;
  is_current?: boolean;
}

/** 新建或修改自定义接口时提交的内容。apiKey 为空表示不改原来的 Key */
export interface CustomEndpointDraft {
  id: string;
  name: string;
  baseURL: string;
  model: string;
  apiKey: string;
  apiMode: string;
  contextLength?: number;
  models: string[];
}

export function emptyEndpointDraft(): CustomEndpointDraft {
  return { id: "", name: "", baseURL: "", model: "", apiKey: "", apiMode: "", models: [] };
}

export function endpointDraft(e: CustomEndpoint): CustomEndpointDraft {
  return { id: e.id, name: e.name, baseURL: e.base_url, model: e.model, apiKey: "", apiMode: e.api_mode ?? "", contextLength: e.context_length, models: e.models ?? [] };
}

export function endpointDraftJSON(d: CustomEndpointDraft): JsonObject {
  const body: JsonObject = { id: d.id, name: d.name, base_url: d.baseURL, model: d.model, api_mode: d.apiMode, discover_models: true };
  if (d.apiKey) body.api_key = d.apiKey;
  if (d.contextLength !== undefined) body.context_length = d.contextLength;
  if (d.models.length) body.models = d.models;
  return body;
}

/** “测试连接”的结果：能连上时带回接口提供的模型列表 */
export interface EndpointProbe {
  ok: boolean;
  reachable?: boolean;
  message?: string;
  models?: string[];
  resolved_base_url?: string;
}

function decodeFail(what: string): HermesError {
  return HermesError.decoding(`${what} 返回的数据无法解析`);
}

function parseEndpoint(raw: unknown): CustomEndpoint | undefined {
  const o = obj(raw);
  const id = s(o?.id);
  if (!o || id === undefined) return undefined;
  return {
    id, name: s(o.name) ?? id, base_url: s(o.base_url) ?? "", model: s(o.model) ?? "", models: strings(o.models),
    api_mode: s(o.api_mode), context_length: int(o.context_length), has_api_key: bool(o.has_api_key),
    api_key_preview: s(o.api_key_preview), is_current: bool(o.is_current),
  };
}

export async function customEndpoints(api: HermesAPI, profile?: string): Promise<CustomEndpoint[]> {
  const r = obj(await api.client.broker.getJSON("/api/providers/custom-endpoints", { profile }));
  return (arr(r?.endpoints) ?? []).map(parseEndpoint).filter((x): x is CustomEndpoint => !!x);
}

/** 保存（新建或修改）一个自定义接口，返回它的 id */
export async function saveCustomEndpoint(api: HermesAPI, draft: CustomEndpointDraft, profile?: string): Promise<string> {
  const r = obj(await api.client.broker.sendJSON("POST", "/api/providers/custom-endpoints", endpointDraftJSON(draft), { profile }));
  return s(r?.id) ?? draft.id;
}

/** 测试连接：请求接口的 /models，顺便拿到模型列表 */
export async function probeCustomEndpoint(api: HermesAPI, draft: CustomEndpointDraft): Promise<EndpointProbe> {
  const o = obj(await api.client.broker.sendJSON("POST", "/api/providers/custom-endpoints/validate", endpointDraftJSON(draft)));
  if (!o) throw decodeFail("validate");
  return { ok: bool(o.ok) ?? false, reachable: bool(o.reachable), message: s(o.message), models: strings(o.models), resolved_base_url: s(o.resolved_base_url) };
}

export async function deleteCustomEndpoint(api: HermesAPI, id: string, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("DELETE", `/api/providers/custom-endpoints/${encodeURIComponent(id)}`, {}, { profile });
}

// MARK: 辅助模型

export interface AuxiliaryTask {
  task: string;
  /** "auto" 表示跟随主模型 */
  provider: string;
  model: string;
  base_url?: string;
  /** 插件注册的任务自带名字和说明 */
  label?: string;
}

export function auxFollowsMain(t: AuxiliaryTask): boolean {
  return !t.provider || t.provider === "auto";
}

export interface AuxiliaryModels {
  tasks: AuxiliaryTask[];
  main?: { provider?: string; model?: string };
}

export async function auxiliaryModels(api: HermesAPI, profile?: string): Promise<AuxiliaryModels> {
  const o = obj(await api.client.broker.getJSON("/api/model/auxiliary", { profile }));
  if (!o) throw decodeFail("auxiliary");
  const main = obj(o.main);
  return {
    tasks: (arr(o.tasks) ?? []).map(obj).flatMap((t) => {
      const task = s(t?.task);
      return t && task ? [{ task, provider: s(t.provider) ?? "", model: s(t.model) ?? "", base_url: s(t.base_url), label: s(t.label) }] : [];
    }),
    main: main ? { provider: s(main.provider), model: s(main.model) } : undefined,
  };
}

/** 给一项辅助任务指定模型；provider = "auto"、model = "" 表示跟随主模型 */
export async function setAuxiliaryModel(api: HermesAPI, task: string, provider: string, model: string, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("POST", "/api/model/set", { scope: "auxiliary", task, provider, model, confirm_expensive_model: true }, { profile });
}

/** 辅助任务的中文名 */
export function auxiliaryTitle(t: AuxiliaryTask): string {
  const names: Record<string, string> = {
    vision: "看图", compression: "压缩上下文", title_generation: "生成标题", skills_hub: "技能搜索", approval: "安全审批",
    mcp: "MCP 工具", review: "复查", voice_chat: "语音对话", triage_specifier: "任务分诊", kanban_decomposer: "看板拆解",
    profile_describer: "机器人简介", curator: "记忆整理",
  };
  return names[t.task] ?? t.label ?? t.task;
}

// MARK: 生图 / 生视频（Hermes 的 image_gen / video_gen 工具）

export interface ToolsetSummary { name: string; label?: string; enabled?: boolean; configured?: boolean }

export interface ToolEnvVar { key: string; prompt?: string; url?: string; is_set?: boolean }

export interface ToolBackend {
  name: string;
  badge?: string;
  tag?: string;
  env_vars?: ToolEnvVar[];
  requires_nous_auth?: boolean;
  is_active?: boolean;
  /** ready / needs_keys / needs_auth / needs_setup */
  status?: string;
}

export interface ToolBackends { providers: ToolBackend[]; active_provider?: string }

export interface ToolModel { id: string; display?: string; speed?: string; strengths?: string; price?: string }

export interface ToolModels { has_models?: boolean; models?: ToolModel[]; current?: string }

export async function toolsets(api: HermesAPI, profile?: string): Promise<ToolsetSummary[]> {
  const raw = arr(await api.client.broker.getJSON("/api/tools/toolsets", { profile }));
  if (!raw) throw decodeFail("toolsets");
  return raw.map(obj).flatMap((t) => {
    const name = s(t?.name);
    return t && name ? [{ name, label: s(t.label), enabled: bool(t.enabled), configured: bool(t.configured) }] : [];
  });
}

export async function setToolsetEnabled(api: HermesAPI, name: string, enabled: boolean, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("PUT", `/api/tools/toolsets/${name}`, { enabled }, { profile });
}

export async function toolBackends(api: HermesAPI, toolset: string, profile?: string): Promise<ToolBackends> {
  const o = obj(await api.client.broker.getJSON(`/api/tools/toolsets/${toolset}/config`, { profile }));
  if (!o) throw decodeFail("toolset config");
  return {
    providers: (arr(o.providers) ?? []).map(obj).flatMap((p) => {
      const name = s(p?.name);
      if (!p || !name) return [];
      return [{
        name, badge: s(p.badge), tag: s(p.tag), status: s(p.status), requires_nous_auth: bool(p.requires_nous_auth), is_active: bool(p.is_active),
        env_vars: (arr(p.env_vars) ?? []).map(obj).flatMap((e) => {
          const key = s(e?.key);
          return e && key ? [{ key, prompt: s(e.prompt), url: s(e.url), is_set: bool(e.is_set) }] : [];
        }),
      }];
    }),
    active_provider: s(o.active_provider),
  };
}

export async function selectToolBackend(api: HermesAPI, toolset: string, backend: string, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("PUT", `/api/tools/toolsets/${toolset}/provider`, { provider: backend }, { profile });
}

/** 保存后端需要的 Key（存在主机的 .env 里） */
export async function saveToolKeys(api: HermesAPI, toolset: string, env: Record<string, string>, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("PUT", `/api/tools/toolsets/${toolset}/env`, { env }, { profile });
}

export async function toolModels(api: HermesAPI, toolset: string, backend?: string, profile?: string): Promise<ToolModels> {
  const o = obj(await api.client.broker.getJSON(`/api/tools/toolsets/${toolset}/models`, { profile, provider: backend }));
  if (!o) throw decodeFail("toolset models");
  return {
    has_models: bool(o.has_models), current: s(o.current),
    models: arr(o.models)?.map(obj).flatMap((m) => {
      const id = s(m?.id);
      return m && id ? [{ id, display: s(m.display), speed: s(m.speed), strengths: s(m.strengths), price: s(m.price) }] : [];
    }),
  };
}

export async function selectToolModel(api: HermesAPI, toolset: string, model: string, backend?: string, profile?: string): Promise<void> {
  const body: JsonObject = { model };
  if (backend) body.provider = backend;
  await api.client.broker.sendJSON("PUT", `/api/tools/toolsets/${toolset}/model`, body, { profile });
}
